import { BaseDialog } from '@/components/BaseDialog'
import { SectionCard } from '@/components/SectionCard'
import { DepartmentItemsSkeleton } from '@/components/skeletons/DepartmentItemsSkeleton'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Spinner } from '@/components/ui/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useGetDepartmentItems } from '@/features/departments/hooks/useDepartment'
import { useUpsertCalibrations } from '@/features/gateways/hooks/useGateway'
import { ItemStats } from '@/features/items/components/ItemStats'
import { Dot } from '@/features/map/types/shape'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { getMutationOptions } from '@/utils/mutation'
import { Info } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TagData } from '../types/trackLocation'
import { GatewayCalibrationPreview } from './GatewayCalibrationPreview'

type GatewayTag = {
  tagId: string
  rssi: number
  battery?: number
  lastSeen: number
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  departmentId?: string | null
  gatewayDot: Dot
  deviceDots?: Dot[]
  tags?: TagData[]
  isLoading?: boolean
}

export function GatewayCalibrate({
  open,
  onOpenChange,
  departmentId,
  gatewayDot,
  deviceDots = [],
  tags = [],
  isLoading,
}: Props) {
  const { t } = useTranslation()
  const { formatDateTime } = useDateFormatter()
  const upsertCalibrations = useUpsertCalibrations()
  const { data: departmentItems, isFetching: isFetchingDepartment } = useGetDepartmentItems(
    departmentId ?? '',
    undefined,
    open,
  )

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [calibrationTags, setCalibrationTags] = useState<GatewayTag[]>([])
  const [distance, setDistance] = useState<1 | 3>(1)

  const items = departmentItems?.items ?? []
  const isInvalid = selectedTagIds.length <= 0

  useEffect(() => {
    if (!open) return
    const now = Date.now()

    setCalibrationTags(prev => {
      const map = new Map(prev.map(tag => [tag.tagId, tag]))

      tags.forEach(tag => {
        const observation = tag.observations.find(o => o.gatewayId === gatewayDot?.referenceId)

        if (!observation) return

        map.set(tag.tagId, {
          tagId: tag.tagId,
          rssi: observation.rssi,
          battery: tag.battery,
          lastSeen: now,
        })
      })

      return [...map.values()]
    })
  }, [tags, gatewayDot?.referenceId, open])

  useEffect(() => {
    if (!open) {
      setSelectedTagIds([])
      setCalibrationTags([])
    }
  }, [open])

  const mappedDeviceDots = deviceDots
    .filter(dot => dot.gatewayId === gatewayDot.id)
    .map(dot => {
      const item = items?.find(i => i.tagId === dot.id)

      return {
        ...dot,
        label: item?.productName ?? dot.label,
        item,
      }
    })

  const gatewayTags = calibrationTags.map(tag => {
    const item = items?.find(i => i.tagId === tag.tagId)

    return {
      tagId: tag.tagId,
      rssi: tag.rssi,
      battery: tag.battery,
      lastSeen: tag.lastSeen,

      name: item?.productName,
      brand: item?.productBrand,
      model: item?.productModel,
    }
  })

  function toggleTag(tagId: string) {
    setSelectedTagIds(prev => (prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]))
  }

  function handleSubmit() {
    if (isInvalid) return
    const options = getMutationOptions({
      successMessage: t('gateway.calibration.toast.calibrateSuccess'),
      onSuccess: () => {
        onOpenChange(false)
      },
    })

    const payload = gatewayTags
      .filter(tag => selectedTagIds.includes(tag.tagId))
      .map(tag => ({
        tagId: tag.tagId,
        distance,
        rssi: tag.rssi,
      }))

    upsertCalibrations.mutate({ id: gatewayDot.id, payload }, options)
  }

  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('gateway.calibration.title')}
      isLoading={isLoading}
      dialogContentClass="sm:max-w-6xl"
    >
      <div className="grid grid-cols-12 h-full">
        {/* LEFT MAP */}
        <div className="col-span-12 overflow-auto p-4 md:col-span-8 md:border-r space-y-4">
          <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
            <div className="flex gap-2">
              <Info className="mt-0.5 size-4 text-blue-500" />
              <p className="text-sm text-muted-foreground">
                {t('gateway.calibration.description', { count: distance })}
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <GatewayCalibrationPreview
              gatewayDot={gatewayDot}
              deviceDots={mappedDeviceDots}
            ></GatewayCalibrationPreview>
          </div>

          <div className="flex gap-6">
            <span className="text-sm font-medium">{t('gateway.calibration.distance')}</span>

            <RadioGroup
              value={distance.toString()}
              onValueChange={value => setDistance(Number(value) as 1 | 3)}
              className="flex flex-row gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="1" id="distance-1" />
                <Label htmlFor="distance-1">1 m</Label>
              </div>

              <div className="flex items-center space-x-2">
                <RadioGroupItem value="3" id="distance-3" />
                <Label htmlFor="distance-3">3 m</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-4">
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/30 hover:bg-transparent">
                    <TableHead className="w-10" />
                    <TableHead className="text-xs text-muted-foreground">{t('gateway.calibration.tagId')}</TableHead>
                    <TableHead className="text-xs text-muted-foreground">
                      {t('gateway.calibration.equipment')}
                    </TableHead>
                    <TableHead className="text-xs text-muted-foreground">{t('gateway.calibration.rssi')}</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Battery</TableHead>
                    <TableHead className="text-xs text-muted-foreground">{t('gateway.calibration.lastSeen')}</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {gatewayTags.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                        {t('gateway.calibration.notFound')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    gatewayTags.map(tag => (
                      <TableRow key={tag.tagId}>
                        <TableCell>
                          <Checkbox
                            checked={selectedTagIds.includes(tag.tagId)}
                            onCheckedChange={() => toggleTag(tag.tagId)}
                          />
                        </TableCell>

                        <TableCell className="font-mono">{tag.tagId}</TableCell>
                        <TableCell className="font-mono">{tag.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{tag.rssi}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{tag.battery ?? '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDateTime(new Date(tag.lastSeen), {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSubmit} disabled={isLoading || isInvalid || upsertCalibrations.isPending}>
                {upsertCalibrations.isPending && <Spinner />}
                {t('common.save')}
              </Button>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-span-12 md:col-span-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-4">
            {/* DEVICES */}

            {isFetchingDepartment ? (
              <DepartmentItemsSkeleton />
            ) : (
              <SectionCard title={t('common.equipment')} headerClass="pb-0">
                {items?.length === 0 ? (
                  <p className="mt-2 text-xs text-muted-foreground">{t('department.noEquipment')}</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {items?.map(item => {
                      return (
                        <ItemStats
                          key={item.id}
                          title={item.productName}
                          subtitle={item.serialNumber}
                          status={item.status}
                          days={item.daysSinceLastStatusChange}
                        ></ItemStats>
                      )
                    })}
                  </div>
                )}
              </SectionCard>
            )}
          </div>
        </div>
      </div>
    </BaseDialog>
  )
}
